import React, { useState, useEffect, useMemo } from "react";
import { Plus, Globe, Trash2, Clock, Search, Loader2, AlertCircle } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const DomainManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadDomains();
  }, [activeAiAgentId]);

  const loadDomains = async () => {
    try {
      setIsFetching(true);
      const response = await api.getDomains();
      setDomains(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error loading domains:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) return;

    // Premium proactive check: clean input and check if it already exists in state
    const cleaned = newDomain.trim().toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
    const isDuplicate = domains.some(d => {
      const dCleaned = d.domain.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
      return dCleaned === cleaned;
    });

    if (isDuplicate) {
      showToast(`Domain '${newDomain}' is already authorized!`, "error");
      return;
    }

    setLoading(true);
    try {
      await api.addDomain(newDomain);
      setNewDomain("");
      await loadDomains();
      showToast("Domain added successfully!", "success");
    } catch (error) {
      showToast(error.message || "Failed to add domain", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteDomain = (domainId) => {
    setDomainToDelete(domainId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!domainToDelete) return;
    setIsDeleting(true);
    try {
      await api.deleteDomain(domainToDelete);
      await loadDomains();
      showToast("Domain deleted", "success");
      setIsDeleteModalOpen(false);
    } catch (error) {
      showToast("Failed to delete domain", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredDomains = useMemo(() => {
    if (!searchQuery.trim()) return domains;
    return domains.filter(d => 
      d.domain?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [domains, searchQuery]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Domain Security</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Govern the architectural boundaries of your AI interface with cryptographic origin validation.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
          
          </div>
        </div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start">
          {/* Left Column: Add Domain & Guidelines */}
          <div className="lg:col-span-4 space-y-4 sm:space-y-5">
            {/* Add New Domain Card */}
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-zinc-200 shadow-2xs">
              <div className="flex items-center justify-between mb-3.5 sm:mb-4">
                <div>
                  <h3 className="font-semibold text-zinc-900 text-xs uppercase tracking-wider">Authorize Origin</h3>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Allowlist host for widget embeds</p>
                </div>
                <Plus className="text-zinc-600 shrink-0" size={18} />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                    Domain / Origin
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      placeholder="e.g. app.yourbrand.com"
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                      onKeyDown={(e) => e.key === "Enter" && addDomain()}
                    />
                  </div>
                </div>

                <button
                  onClick={addDomain}
                  disabled={loading || !newDomain.trim()}
                  className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold text-xs shadow-xs cursor-pointer active:scale-[0.99]"
                >
                  {loading ? (
                    <>
                      <Loader2 size={14} className="animate-spin text-zinc-400" />
                      <span>Authorizing...</span>
                    </>
                  ) : (
                    <>
                      <Plus size={13} strokeWidth={2.5} />
                      <span>Authorize Domain</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Quick Context Card */}
            <div className="bg-zinc-50/70 rounded-xl p-3.5 sm:p-5 border border-zinc-200/80">
              <div className="flex items-start gap-2.5">
                <AlertCircle size={15} className="text-zinc-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-semibold text-zinc-900">Security Rule</h4>
                  <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                    Embedded chat components reject requests originating from unverified domains to prevent unauthorized token consumption and session hijacking.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Domains List */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-xl border border-zinc-200 shadow-2xs overflow-hidden">
              {/* Header Bar */}
              <div className="p-3.5 sm:p-5 border-b border-zinc-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white">
                <div className="flex items-center gap-2.5">
                  <Globe className="text-zinc-600 shrink-0" size={18} />
                  <div>
                    <h3 className="font-semibold text-zinc-900 text-xs uppercase tracking-wider">Authorized Origins</h3>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      {domains.length} {domains.length === 1 ? "domain" : "domains"} verified
                    </p>
                  </div>
                </div>

                {/* Search Bar */}
                {domains.length > 0 && (
                  <div className="relative w-full sm:w-64">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                      type="text"
                      placeholder="Search origins..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400 hover:text-zinc-700 font-medium"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Domains Content */}
              {isFetching ? (
                <div className="py-12 sm:py-16 flex flex-col items-center justify-center text-zinc-400">
                  <Loader2 size={24} className="animate-spin mb-2 text-zinc-600" />
                  <span className="text-xs font-medium">Verifying domains...</span>
                </div>
              ) : domains.length === 0 ? (
                <div className="text-center py-12 sm:py-16 px-4 bg-zinc-50/40">
                  <Globe size={22} className="text-zinc-400 shrink-0" />
                  <h4 className="text-xs font-semibold text-zinc-900">No Domains Authorized</h4>
                  <p className="text-[11px] text-zinc-500 mt-1 max-w-sm mx-auto">
                    Add your website or application domain in the left panel to allow widget mounting.
                  </p>
                </div>
              ) : filteredDomains.length === 0 ? (
                <div className="text-center py-10 sm:py-12 px-4 bg-zinc-50/30">
                  <p className="text-xs text-zinc-500 font-medium">No domains match "{searchQuery}"</p>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="mt-2 text-[11px] text-zinc-900 underline font-semibold cursor-pointer"
                  >
                    Reset filter
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {filteredDomains.map((domain) => (
                    <div
                      key={domain.id}
                      className="p-3 sm:p-4 hover:bg-zinc-50/70 transition-colors flex items-center justify-between gap-3 group"
                    >
                      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                        <Globe className="text-zinc-600 shrink-0" size={18} />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                            <span className="text-xs font-semibold text-zinc-900 truncate max-w-[180px] sm:max-w-xs md:max-w-sm">
                              {domain.domain}
                            </span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 text-[10px] font-medium border border-zinc-200">
                              Active Origin
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-[10px] text-zinc-400 mt-0.5 font-medium">
                            <Clock size={10} className="shrink-0" />
                            <span className="truncate">
                              Authorized {domain.created_at ? new Date(domain.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : "recently"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => deleteDomain(domain.id)}
                          className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer touch-manipulation"
                          title="Revoke origin access"
                          aria-label="Delete domain"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table Footer */}
              {domains.length > 0 && (
                <div className="px-3.5 sm:px-4 py-2.5 sm:py-3 bg-zinc-50/60 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-400 font-medium">
                  <span>{filteredDomains.length} of {domains.length} origins</span>
                  <span className="text-[10px] text-zinc-400 hidden sm:inline">Encrypted CORS Whitelist</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Revoke Origin Authorization"
        message="This will immediately block the chat widget from initializing on this origin. Confirm to proceed."
        confirmText="Revoke Domain"
      />
    </div>
  );
};

export default DomainManagement;
